import React, { useEffect, useState } from 'react';
import AppShell from '@/components/layout/AppShell';
import { ownerNav } from '@/lib/navConfig';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import KPICard from '@/components/common/KPICard';
import { Wallet, ShoppingBag, TrendingUp, Download, Receipt, PiggyBank } from 'lucide-react';
import api, { fmtIDR, currentMonth, API } from '@/lib/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

const MONTH_ID = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
function monthLabel(m) { try { const [y, mm] = m.split('-'); return `${MONTH_ID[Number(mm) - 1]} ${y}`; } catch { return m; } }

export default function OwnerReports() {
  const [month, setMonth] = useState(currentMonth());
  const [summary, setSummary] = useState(null);
  const [daily, setDaily] = useState([]);
  const [best, setBest] = useState([]);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const load = async () => {
      const [s, d, b] = await Promise.all([
        api.get(`/reports/sales-summary?month=${month}`),
        api.get(`/reports/daily-sales?month=${month}`),
        api.get(`/reports/best-selling?month=${month}&limit=10`),
      ]);
      setSummary(s.data);
      setDaily(d.data);
      setBest(b.data);
    };
    load();
  }, [month]);

  const downloadReport = async (ppnFilter = 'all') => {
    setDownloading(true);
    try {
      const resp = await api.get(`/reports/monthly-report.pdf?month=${month}&ppn_filter=${ppnFilter}`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const suffix = ppnFilter === 'include' ? 'IncPPN' : ppnFilter === 'exclude' ? 'ExcPPN' : 'Global';
      a.download = `LaporanKeuangan-${month}-${suffix}-Owner-Laba.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Laporan ${suffix} ${monthLabel(month)} diunduh`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal mengunduh laporan');
    } finally { setDownloading(false); }
  };

  return (
    <AppShell nav={ownerNav} title="Laporan Owner" showBell>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end sm:justify-between">
          <div className="max-w-xs w-full sm:w-auto">
            <Label>Bulan</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="reports-month" />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => downloadReport('all')} disabled={downloading} variant="default" data-testid="download-monthly-report-owner">
              <Download className="h-4 w-4 mr-2" /> Laporan Global
            </Button>
            <Button onClick={() => downloadReport('include')} disabled={downloading} variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-50" data-testid="download-monthly-report-inc-ppn">
              <Download className="h-4 w-4 mr-2" /> Inc PPN 11%
            </Button>
            <Button onClick={() => downloadReport('exclude')} disabled={downloading} variant="outline" className="border-sky-300 text-sky-700 hover:bg-sky-50" data-testid="download-monthly-report-exc-ppn">
              <Download className="h-4 w-4 mr-2" /> Non-PPN
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          <KPICard label="Omset" value={fmtIDR(summary?.revenue || 0)} icon={Wallet} tone="info" />
          <KPICard label="COGS (HPP)" value={fmtIDR(summary?.cogs || 0)} icon={ShoppingBag} tone="warning" />
          <KPICard label="Laba Kotor" value={fmtIDR(summary?.gross_profit || 0)} icon={TrendingUp} tone="success" />
          <KPICard label="Pengeluaran" value={fmtIDR(summary?.expenses || 0)} icon={Receipt} tone="danger" />
          <KPICard label="Laba Bersih" value={fmtIDR(summary?.net_profit || 0)} icon={PiggyBank} tone={(summary?.net_profit || 0) >= 0 ? 'success' : 'danger'} />
          <KPICard label="Transaksi" value={(summary?.transactions || 0).toLocaleString('id-ID')} />
        </div>

        <Card>
          <CardHeader><CardTitle>Penjualan Harian</CardTitle></CardHeader>
          <CardContent>
            {daily.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">Tidak ada data</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d?.slice(8, 10)} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1e6).toFixed(0)}jt`} />
                  <Tooltip formatter={(v) => fmtIDR(v)} />
                  <Bar dataKey="revenue" fill="hsl(186 86% 28%)" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Barang Terlaris</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>#</TableHead><TableHead>SKU</TableHead><TableHead>Nama</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Omset</TableHead></TableRow></TableHeader>
              <TableBody>
                {best.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8">Belum ada data</TableCell></TableRow>
                ) : best.map((b, i) => (
                  <TableRow key={b.product_id}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell className="font-mono text-xs">{b.sku}</TableCell>
                    <TableCell>{b.name}</TableCell>
                    <TableCell className="text-right tabular-nums">{b.quantity}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtIDR(b.revenue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
